import React from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@material-ui/core';
import { ArrowBack, Close } from '@material-ui/icons';
import { TabletButton } from '@campfire/tablet-button';
import { CampfireRichEditor } from '@campfire/campfire-rich-editor';
import { EditorState } from 'draft-js';
import { Formik } from 'formik';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';
import { useCampfireQuery } from '../../../../global/network/useCampfireQuery';
import { ActivityFormV2 } from '../../../program-manager/activity-management/activity-form-v2/ActivityFormV2';
import { RosterTemplateForm } from '../../../program-manager/rosters/roster-templates-screen/RosterTemplateForm';
import { AutoLocationField } from '../../../../common/auto-location-field/AutoLocationField';
import { EntityType } from './form-responses-types';
import { useSendCalendarInvite } from './send-calendar-invite-api';
import { useSnackbar } from '../../../../global/config/useSnackbar';
import { useUser } from '../../../../global/auth/useUser';
import { GET_ORG_COMM_PREFERENCES } from '../../admin-console/Communications/Preferences/admin-console-preferences-model.gql';
import { GetOrgCommPreferences } from '../../admin-console/Communications/Preferences/__generated__/GetOrgCommPreferences';

interface FormWizardDialogProps {
  open: boolean;
  wizardStep: number;
  selectedEntityType: EntityType | null;
  activityPrefilledValues: any | undefined;
  rosterTemplatePrefilledValues: any | undefined;
  emailPrefilledValues: any | undefined;
  calendarInvitePrefilledValues: any | undefined;
  onClose: () => void;
  onStepChange: (step: number) => void;
  onEntityTypeChange: (type: EntityType | null) => void;
  onEntityCreated: (entityType: EntityType, entityId: string) => void;
  onSuccess: () => void;
}

export const FormWizardDialog: React.FC<FormWizardDialogProps> = ({
  open,
  wizardStep,
  selectedEntityType,
  activityPrefilledValues,
  rosterTemplatePrefilledValues,
  emailPrefilledValues,
  calendarInvitePrefilledValues,
  onClose,
  onStepChange,
  onEntityTypeChange,
  onEntityCreated,
  onSuccess,
}) => {
  const { theme } = useCampfireTheme();
  const { setSnackbar } = useSnackbar();
  const { user } = useUser();
  const { data: orgCommPreferenceData } = useCampfireQuery<GetOrgCommPreferences, undefined>(GET_ORG_COMM_PREFERENCES);
  const defaultReplyToEmail = orgCommPreferenceData?.tm.tenant.config?.communication?.replyTo || 'noreply@volaby.org';
  const { send: sendCalendarInvite } = useSendCalendarInvite();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={wizardStep === 1 ? 'lg' : 'sm'}
      fullWidth
      fullScreen={wizardStep === 1}
    >
      {wizardStep === 0 ? (
        <DialogTitle style={{ paddingBottom: 0 }}>
          <Grid container justify='space-between'>
            <Grid item>
              <Typography
                style={{
                  fontSize: '23px',
                  fontWeight: 550,
                  color: theme.color.grey.neutral400,
                  paddingLeft: '5px',
                }}
              >
                Form Wizard
              </Typography>
            </Grid>
            <Grid item>
              <IconButton size='small' onClick={onClose}>
                <Close color='action' />
              </IconButton>
            </Grid>
          </Grid>
          <Box pr={3} mt={1}>
            <Typography style={{ fontSize: 14, paddingLeft: '5px', color: theme.color.grey.neutral300 }}>
              Create an entity from this form submission. Select what you would like to create.
            </Typography>
          </Box>
        </DialogTitle>
      ) : (
        <DialogTitle style={{ padding: '8px 16px', borderBottom: `1px solid ${theme.color.grey.neutralBrand200}` }}>
          <Grid container justify='space-between' alignItems='center'>
            <Grid item>
              <Box display='flex' alignItems='center' style={{ gap: '8px' }}>
                <TabletButton
                  variant='text'
                  size='small'
                  onClick={() => onStepChange(0)}
                  style={{ minWidth: 'auto', padding: '4px 8px' }}
                >
                  <ArrowBack style={{ fontSize: '18px' }} />
                </TabletButton>
                <Typography
                  style={{
                    fontSize: '16px',
                    fontWeight: 600,
                    color: theme.color.grey.neutral400,
                  }}
                >
                  Create{' '}
                  {selectedEntityType === 'activity'
                    ? 'Activity'
                    : selectedEntityType === 'roster_template'
                    ? 'Roster Template'
                    : selectedEntityType === 'email'
                    ? 'Email'
                    : 'Calendar Invite'}
                </Typography>
              </Box>
            </Grid>
            <Grid item>
              <IconButton size='small' onClick={onClose}>
                <Close color='action' />
              </IconButton>
            </Grid>
          </Grid>
        </DialogTitle>
      )}
      <DialogContent>
        {wizardStep === 0 && (
          <Box py={2}>
            <Box display='flex' flexDirection='column' style={{ gap: '12px' }}>
              <Box
                onClick={() => onEntityTypeChange('activity')}
                style={{
                  border: `2px solid ${
                    selectedEntityType === 'activity' ? theme.palette.primary.main : theme.color.grey.neutralBrand200
                  }`,
                  borderRadius: '8px',
                  padding: '16px 20px',
                  cursor: 'pointer',
                  backgroundColor: selectedEntityType === 'activity' ? 'rgba(99, 136, 137, 0.05)' : '#fff',
                  transition: 'all 0.2s ease',
                }}
              >
                <Box display='flex' alignItems='center' style={{ gap: '12px' }}>
                  <Box
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '8px',
                      backgroundColor:
                        selectedEntityType === 'activity'
                          ? theme.palette.primary.main
                          : theme.color.grey.neutralBrand100,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <svg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                      <path
                        d='M19 3H5C3.89 3 3 3.9 3 5V19C3 20.1 3.89 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM17 13H13V17H11V13H7V11H11V7H13V11H17V13Z'
                        fill={selectedEntityType === 'activity' ? '#fff' : '#646464'}
                      />
                    </svg>
                  </Box>
                  <Box>
                    <Typography style={{ fontWeight: 600, fontSize: '0.95rem', color: theme.color.grey.neutral400 }}>
                      Activity
                    </Typography>
                    <Typography style={{ fontSize: '0.8rem', color: theme.color.grey.neutral300 }}>
                      Create a new volunteer activity from this submission
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                onClick={() => onEntityTypeChange('roster_template')}
                style={{
                  border: `2px solid ${
                    selectedEntityType === 'roster_template'
                      ? theme.palette.primary.main
                      : theme.color.grey.neutralBrand200
                  }`,
                  borderRadius: '8px',
                  padding: '16px 20px',
                  cursor: 'pointer',
                  backgroundColor: selectedEntityType === 'roster_template' ? 'rgba(99, 136, 137, 0.05)' : '#fff',
                  transition: 'all 0.2s ease',
                }}
              >
                <Box display='flex' alignItems='center' style={{ gap: '12px' }}>
                  <Box
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '8px',
                      backgroundColor:
                        selectedEntityType === 'roster_template'
                          ? theme.palette.primary.main
                          : theme.color.grey.neutralBrand100,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <svg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                      <path
                        d='M19 3H14.82C14.4 1.84 13.3 1 12 1C10.7 1 9.6 1.84 9.18 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM12 3C12.55 3 13 3.45 13 4C13 4.55 12.55 5 12 5C11.45 5 11 4.55 11 4C11 3.45 11.45 3 12 3ZM14 17H7V15H14V17ZM17 13H7V11H17V13ZM17 9H7V7H17V9Z'
                        fill={selectedEntityType === 'roster_template' ? '#fff' : '#646464'}
                      />
                    </svg>
                  </Box>
                  <Box>
                    <Typography style={{ fontWeight: 600, fontSize: '0.95rem', color: theme.color.grey.neutral400 }}>
                      Roster Template
                    </Typography>
                    <Typography style={{ fontSize: '0.8rem', color: theme.color.grey.neutral300 }}>
                      Create a roster template for scheduling volunteers
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                onClick={() => onEntityTypeChange('email')}
                style={{
                  border: `2px solid ${
                    selectedEntityType === 'email' ? theme.palette.primary.main : theme.color.grey.neutralBrand200
                  }`,
                  borderRadius: '8px',
                  padding: '16px 20px',
                  cursor: 'pointer',
                  backgroundColor: selectedEntityType === 'email' ? 'rgba(99, 136, 137, 0.05)' : '#fff',
                  transition: 'all 0.2s ease',
                }}
              >
                <Box display='flex' alignItems='center' style={{ gap: '12px' }}>
                  <Box
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '8px',
                      backgroundColor:
                        selectedEntityType === 'email' ? theme.palette.primary.main : theme.color.grey.neutralBrand100,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <svg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                      <path
                        d='M20 4H4C2.9 4 2.01 4.9 2.01 6L2 18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4ZM20 8L12 13L4 8V6L12 11L20 6V8Z'
                        fill={selectedEntityType === 'email' ? '#fff' : '#646464'}
                      />
                    </svg>
                  </Box>
                  <Box>
                    <Typography style={{ fontWeight: 600, fontSize: '0.95rem', color: theme.color.grey.neutral400 }}>
                      Email
                    </Typography>
                    <Typography style={{ fontSize: '0.8rem', color: theme.color.grey.neutral300 }}>
                      Send an email based on this submission
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                onClick={() => onEntityTypeChange('calendar_invite')}
                style={{
                  border: `2px solid ${
                    selectedEntityType === 'calendar_invite'
                      ? theme.palette.primary.main
                      : theme.color.grey.neutralBrand200
                  }`,
                  borderRadius: '8px',
                  padding: '16px 20px',
                  cursor: 'pointer',
                  backgroundColor: selectedEntityType === 'calendar_invite' ? 'rgba(99, 136, 137, 0.05)' : '#fff',
                  transition: 'all 0.2s ease',
                }}
              >
                <Box display='flex' alignItems='center' style={{ gap: '12px' }}>
                  <Box
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '8px',
                      backgroundColor:
                        selectedEntityType === 'calendar_invite'
                          ? theme.palette.primary.main
                          : theme.color.grey.neutralBrand100,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <svg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                      <path
                        d='M19 4H18V2H16V4H8V2H6V4H5C3.89 4 3.01 4.9 3.01 6L3 20C3 21.1 3.89 22 5 22H19C20.1 22 21 21.1 21 20V6C21 4.9 20.1 4 19 4ZM19 20H5V9H19V20ZM7 11H12V16H7V11Z'
                        fill={selectedEntityType === 'calendar_invite' ? '#fff' : '#646464'}
                      />
                    </svg>
                  </Box>
                  <Box>
                    <Typography style={{ fontWeight: 600, fontSize: '0.95rem', color: theme.color.grey.neutral400 }}>
                      Calendar Invite
                    </Typography>
                    <Typography style={{ fontSize: '0.8rem', color: theme.color.grey.neutral300 }}>
                      Create a calendar event from this submission
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        )}
        {wizardStep === 1 && selectedEntityType === 'activity' && (
          <Box style={{ height: 'calc(100vh - 200px)', overflow: 'auto' }}>
            <ActivityFormV2
              onSuccess={(activityId) => {
                onEntityCreated('activity', activityId);
                onSuccess();
              }}
              predefinedFormValues={activityPrefilledValues}
            />
          </Box>
        )}
        {wizardStep === 1 && selectedEntityType === 'roster_template' && (
          <Box style={{ height: 'calc(100vh - 200px)', overflow: 'auto' }}>
            <RosterTemplateForm
              onSuccess={() => {
                onSuccess();
              }}
              predefinedFormValues={rosterTemplatePrefilledValues}
              enableFlexibleSessions
              onCreated={(creationResult) => {
                if (creationResult.rosterTemplateIds.length > 0) {
                  onEntityCreated('roster_template', creationResult.rosterTemplateIds[0]);
                }
              }}
            />
          </Box>
        )}
        {wizardStep === 1 && selectedEntityType === 'email' && (
          <Box style={{ height: 'calc(100vh - 200px)', overflow: 'auto', padding: '24px' }}>
            <Formik
              initialValues={{
                subject: emailPrefilledValues?.subject || '',
                replyTo: 'default',
                message: emailPrefilledValues?.message || EditorState.createEmpty(),
              }}
              onSubmit={(values) => {
                console.log('Email values:', values);
                onEntityCreated('email', 'temp-email-id');
                onSuccess();
              }}
            >
              {(formik) => (
                <Box>
                  <Typography variant='h6' style={{ marginBottom: '24px' }}>
                    Compose Email
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label='Subject'
                        variant='outlined'
                        value={formik.values.subject}
                        onChange={(e) => formik.setFieldValue('subject', e.target.value)}
                        placeholder='Enter email subject'
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl variant='outlined' fullWidth>
                        <InputLabel>Reply to</InputLabel>
                        <Select
                          value={formik.values.replyTo}
                          onChange={(e) => formik.setFieldValue('replyTo', e.target.value as string)}
                          label='Reply to'
                        >
                          <MenuItem value='default'>Default Reply ({defaultReplyToEmail})</MenuItem>
                          <MenuItem value='current'>
                            {user.firstName} {user.lastName} ({user.email})
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant='subtitle2' style={{ marginBottom: '8px', color: '#666' }}>
                        Message
                      </Typography>
                      <Box style={{ border: '1px solid #ccc', borderRadius: '4px', minHeight: '300px' }}>
                        <CampfireRichEditor
                          editorState={formik.values.message}
                          setEditorState={(state) => formik.setFieldValue('message', state)}
                          placeholder='Compose your message...'
                        />
                      </Box>
                    </Grid>
                  </Grid>
                  <Box mt={3} display='flex' justifyContent='flex-end' style={{ gap: '12px' }}>
                    <TabletButton variant='text' onClick={onClose}>
                      Cancel
                    </TabletButton>
                    <TabletButton variant='contained' onClick={() => formik.handleSubmit()}>
                      Send Email
                    </TabletButton>
                  </Box>
                </Box>
              )}
            </Formik>
          </Box>
        )}
        {wizardStep === 1 && selectedEntityType === 'calendar_invite' && (
          <Box style={{ height: 'calc(100vh - 200px)', overflow: 'auto', padding: '24px' }}>
            <Formik
              initialValues={{
                attendeeEmail: calendarInvitePrefilledValues?.attendeeEmail || '',
                title: calendarInvitePrefilledValues?.title || '',
                date: calendarInvitePrefilledValues?.date || '',
                startTime: calendarInvitePrefilledValues?.startTime || '',
                endTime: calendarInvitePrefilledValues?.endTime || '',
                meetingType:
                  (calendarInvitePrefilledValues?.meetingType as 'in_person' | 'online' | 'phone') || 'in_person',
                location: calendarInvitePrefilledValues?.location || {
                  description: '',
                  formatted: '',
                  latitude: 0,
                  longitude: 0,
                  placesId: '',
                },
                meetingLink: calendarInvitePrefilledValues?.meetingLink || '',
                phoneNumber: calendarInvitePrefilledValues?.phoneNumber || '',
                notes: calendarInvitePrefilledValues?.notes || '',
              }}
              onSubmit={async (values) => {
                if (!values.attendeeEmail || !values.title || !values.date || !values.startTime || !values.endTime) {
                  setSnackbar({ open: true, message: 'Please fill in all required fields', variant: 'error' });
                  return;
                }

                const startDateTime = `${values.date}T${values.startTime}:00`;
                const endDateTime = `${values.date}T${values.endTime}:00`;

                const locationText =
                  values.meetingType === 'in_person' ? values.location.description || values.location.formatted : null;

                const result = await sendCalendarInvite({
                  attendeeEmail: values.attendeeEmail,
                  title: values.title,
                  description: values.notes || '',
                  startDateTime,
                  endDateTime,
                  location: locationText,
                  meetingLink: values.meetingType === 'online' ? values.meetingLink : null,
                  phoneNumber: values.meetingType === 'phone' ? values.phoneNumber : null,
                });

                if (result.ok) {
                  setSnackbar({ open: true, message: 'Calendar invite sent successfully', variant: 'success' });
                  onEntityCreated('calendar_invite', 'calendar-invite-sent');
                  onSuccess();
                } else {
                  setSnackbar({ open: true, message: 'Failed to send calendar invite', variant: 'error' });
                }
              }}
            >
              {(formik) => (
                <Box>
                  <Typography variant='h6' style={{ marginBottom: '24px' }}>
                    Create Calendar Event
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label='Attendee Email'
                        variant='outlined'
                        type='email'
                        value={formik.values.attendeeEmail}
                        onChange={(e) => formik.setFieldValue('attendeeEmail', e.target.value)}
                        placeholder='email@example.com'
                        required
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label='Event Title'
                        variant='outlined'
                        value={formik.values.title}
                        onChange={(e) => formik.setFieldValue('title', e.target.value)}
                        placeholder='Enter event title'
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label='Date'
                        type='date'
                        variant='outlined'
                        value={formik.values.date}
                        onChange={(e) => formik.setFieldValue('date', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        label='Start Time'
                        type='time'
                        variant='outlined'
                        value={formik.values.startTime}
                        onChange={(e) => formik.setFieldValue('startTime', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        label='End Time'
                        type='time'
                        variant='outlined'
                        value={formik.values.endTime}
                        onChange={(e) => formik.setFieldValue('endTime', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth variant='outlined'>
                        <InputLabel>Meeting Type</InputLabel>
                        <Select
                          value={formik.values.meetingType}
                          onChange={(e) =>
                            formik.setFieldValue('meetingType', e.target.value as 'in_person' | 'online' | 'phone')
                          }
                          label='Meeting Type'
                        >
                          <MenuItem value='in_person'>In-Person</MenuItem>
                          <MenuItem value='online'>Online</MenuItem>
                          <MenuItem value='phone'>Phone</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    {formik.values.meetingType === 'in_person' && (
                      <Grid item xs={12}>
                        <AutoLocationField name='location' label='Location' helper fullWidth variant='outlined' />
                      </Grid>
                    )}
                    {formik.values.meetingType === 'online' && (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label='Meeting Link'
                          variant='outlined'
                          value={formik.values.meetingLink}
                          onChange={(e) => formik.setFieldValue('meetingLink', e.target.value)}
                          placeholder='https://zoom.us/j/...'
                        />
                      </Grid>
                    )}
                    {formik.values.meetingType === 'phone' && (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label='Phone Number'
                          variant='outlined'
                          value={formik.values.phoneNumber}
                          onChange={(e) => formik.setFieldValue('phoneNumber', e.target.value)}
                          placeholder='+1 (555) 123-4567'
                        />
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label='Notes (Optional)'
                        variant='outlined'
                        value={formik.values.notes}
                        onChange={(e) => formik.setFieldValue('notes', e.target.value)}
                        placeholder='Add any additional details...'
                      />
                    </Grid>
                  </Grid>
                  <Box mt={3} display='flex' justifyContent='flex-end' style={{ gap: '12px' }}>
                    <TabletButton variant='text' onClick={onClose}>
                      Cancel
                    </TabletButton>
                    <TabletButton variant='contained' onClick={() => formik.handleSubmit()}>
                      Create Event
                    </TabletButton>
                  </Box>
                </Box>
              )}
            </Formik>
          </Box>
        )}
      </DialogContent>
      {wizardStep === 0 && (
        <DialogActions>
          <Box padding={2} display='flex' style={{ gap: '8px' }}>
            <TabletButton variant='text' color='error' onClick={onClose}>
              Cancel
            </TabletButton>
            <TabletButton variant='contained' disabled={!selectedEntityType} onClick={() => onStepChange(1)}>
              Continue
            </TabletButton>
          </Box>
        </DialogActions>
      )}
    </Dialog>
  );
};
